// The AI roof analysis on an ordered property (Neil, 10 Oct 2026: "this is a USP we have"): its
// own card under the preliminary checks. The verdict as a badge, how confident it is on a
// red/amber/green meter, the roof described for an installer (shape, layout, likely covering,
// features) and a plain summary. Everything here comes from the API's solar check
// (common/checks.py → roof_check.py) and never names the model or says how the roof was seen.
import { CheckCircle2, CircleHelp, ScanSearch, TriangleAlert } from "lucide-react";
import type { PreliminaryCheck } from "../api";

type Ai = NonNullable<PreliminaryCheck["ai"]>;

const VERDICT = {
  no: { label: "No solar panels found", Icon: CheckCircle2 },
  yes: { label: "Solar panels found", Icon: TriangleAlert },
  unsure: { label: "Not conclusive", Icon: CircleHelp },
};

const SHAPES: Record<string, string> = { pitched: "Pitched", flat: "Flat", mixed: "Pitched and flat", curved: "Curved", unclear: "Unclear" };

/** Confidence bands: red under 50%, amber to 75%, green above. */
const band = (pct: number) => (pct < 50 ? "low" : pct < 75 ? "medium" : "high");
const BAND_LABEL = { low: "Low confidence", medium: "Medium confidence", high: "High confidence" };

const capital = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

export function AiRoofAnalysis({
  ai,
  imageryDate,
  kicker,
  contracted,
  teamNote,
}: {
  ai: Ai;
  imageryDate?: string | null;
  kicker?: string;
  /** Solar contracted, installed or planned for this roof (the research): the photo may predate it */
  contracted?: boolean;
  /** What our team's roof check said (the solar check's detail), as the report's "Our team" row */
  teamNote?: string | null;
}) {
  const pct = Math.max(0, Math.min(100, Math.round(ai.confidence * 100)));
  const level = band(pct);
  const verdict = VERDICT[ai.result];
  const roof = ai.roof;
  // The facts grid: only what the analysis could tell
  const facts = [
    ["Roof shape", roof?.shape ? SHAPES[roof.shape] : null],
    ["Layout", roof?.layout ? capital(roof.layout) : null],
    ["Likely covering", roof?.covering ? capital(roof.covering) : null],
  ].filter((f): f is [string, string] => Boolean(f[1]));
  // Existing solar first, on its own row: what the analysis is for
  const existing =
    ai.result === "yes"
      ? ai.coverage && ai.coverage !== "none"
        ? `Yes, ${ai.coverage} coverage`
        : "Yes"
      : ai.result === "no"
        ? "None found"
        : "Not conclusive";

  return (
    <section className="card airoof" aria-labelledby="airoof-title">
      <header className="airoof-head">
        <span className="airoof-mark" aria-hidden="true">
          <ScanSearch size={20} />
        </span>
        <div className="airoof-title">
          {kicker && <p className="pk-kicker">{kicker}</p>}
          <h2 id="airoof-title">AI roof analysis</h2>
          <p className="muted">
            {ai.scope === "site" ? "Every roof on the site" : "This property's roof"}, from high-resolution aerial imagery
            {imageryDate ? ` (${imageryDate})` : ""}
          </p>
        </div>
        <span className={`airoof-verdict is-${ai.result}`}>
          <verdict.Icon size={16} aria-hidden="true" /> {verdict.label}
        </span>
      </header>

      {/* How sure it is: a banded meter with a marker */}
      <div className="airoof-confidence">
        <div className="airoof-confidence-label">
          <span>Confidence</span>
          <b className={`is-${level}`}>
            {pct}% · {BAND_LABEL[level]}
          </b>
        </div>
        <div className="airoof-meter" role="meter" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct} aria-label={`Confidence ${pct}%`}>
          <span className="seg-low" />
          <span className="seg-medium" />
          <span className="seg-high" />
          <span className="airoof-marker" style={{ left: `${pct}%` }} />
        </div>
        <div className="airoof-scale muted" aria-hidden="true">
          <span>0</span>
          <span style={{ left: "50%" }}>50</span>
          <span style={{ left: "75%" }}>75</span>
          <span>100</span>
        </div>
      </div>

      {/* Solar contracted after the photo was taken: the roof may now carry panels (as the report) */}
      {contracted && (
        <p className="pk-callout is-red">
          <span>
            <b>Solar contracted after this imagery.</b> The photo predates the contract found in Section 11: the roof may now carry panels.
          </span>
        </p>
      )}

      <dl className="airoof-facts">
        <div className={`airoof-existing is-${ai.result}`}>
          <dt>Existing solar</dt>
          <dd>{existing}</dd>
          {ai.locationNote && (
            <p className="airoof-note">This building is one part of a larger site, so our team will also look at its roof while preparing your pack.</p>
          )}
        </div>
        {facts.map(([k, v]) => (
          <div key={k}>
            <dt>{k}</dt>
            <dd>{v}</dd>
          </div>
        ))}
        {/* The roof in a sentence or two, full width under the facts */}
        {ai.summary && (
          <div className="airoof-details">
            <dt>Roof details</dt>
            <dd>{ai.summary}</dd>
          </div>
        )}
      </dl>

      {roof && roof.features.length > 0 && (
        <div className="airoof-features">
          <span className="airoof-features-label">Roof features</span>
          <ul>
            {roof.features.map((f) => (
              <li key={f}>{capital(f)}</li>
            ))}
          </ul>
        </div>
      )}

      {roof?.asbestos === "possible" && (
        <p className="airoof-asbestos">
          <b>Possible asbestos:</b> the roof looks like fibre-cement sheeting, common on older buildings. Have it tested before any work.
        </p>
      )}
      {roof?.access && (
        <p className="airoof-condition">
          <b>Access:</b> {capital(roof.access)}
        </p>
      )}
      {roof?.condition && (
        <p className="airoof-condition">
          <b>Worth noting:</b> {capital(roof.condition)}
        </p>
      )}

      {teamNote && (
        <p className="airoof-condition">
          <b>Our team:</b> {teamNote}
        </p>
      )}
      <p className="muted eo-note">Planning, the local grid, the building's height and the roof's aspect are in Site and constraints (Section 2).</p>

      <p className="airoof-foot muted">
        An automated analysis to help you prepare. Anything it flags is checked by our team, and it isn't a substitute for a site survey.
      </p>
    </section>
  );
}
